import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { DEFAULT_LOCALE, LANGUAGES } from "~core/constants/languange.constants";
import { LanguageCode } from "~core/types/language.type";
import { LOCAL_STORAGE } from "~core/providers/locale-storage";
import { ICountry } from "~core/models/countries.model";
import { httpResource } from "@angular/common/http";
import { getEndpoints } from "~core/constants/endpoints.constants";
import { CountryDialogService } from '~core/services/ui/country-dialog.service';
import { RequiredShipmentService } from "~shared/services/required_shipment.service";
import { AuthService } from '~core/services/auth.service';
import { ProfileService } from '~core/services/profile.service';
import { UserProfile } from '~core/models/profile.model';

@Injectable({
    providedIn: 'root'
})
export class SessionService {
    private readonly LANGUAGE_KEY = 'dfidefaultlanguage';
    private readonly COUNTRY_KEY = 'dfi_current_country_iso';
    private readonly ZIP_KEY = 'dfi_current_zip';

    private translate = inject(TranslateService);
    private readonly localStorage: Storage | null = inject(LOCAL_STORAGE);
    private readonly endpoints = getEndpoints();
    private readonly countryDialog = inject(CountryDialogService);
    private readonly requiredShipmentService = inject(RequiredShipmentService);
    private readonly authService = inject(AuthService);
    private readonly profileService = inject(ProfileService);

    private _companycountries = signal<ICountry[]>([]);
    readonly CompanyCountries = this._companycountries.asReadonly();

    // Esposizione pubblica dello stato dell'utente collegato a ProfileService
    readonly currentUser = computed<UserProfile | null>(() => this.profileService.userProfile());

    // Helper per sapere se l'utente è autorizzato/loggato
    readonly isLoggedIn = computed(() => this.authService.isAuthorized());

    private _currentlanguage = signal<string>(this.getDefaultLanguage());
    readonly CurrentLanguage = this._currentlanguage.asReadonly();

    // ✅ Solo questi utenti devono gestire il paese destinazione in home/header
    readonly requiresDestinationCountry = computed(() => this.profileService.requiresDestinationCountry());

    private _currentCountry = signal<ICountry | null>(null);
    readonly CurrentCountry = this._currentCountry.asReadonly();

    private _currentZip = signal<string | null>(null);
    readonly CurrentZip = this._currentZip.asReadonly();

    readonly CurrentIva = computed(() => this.CurrentCountry()?.vat ?? 0);
    readonly CurrentIvaReduced = computed(() => this.CurrentCountry()?.vat_reduced ?? 0);
    readonly CurrentIvaReduced2 = computed(() => this.CurrentCountry()?.vat_reduced_2 ?? 0);

    private _is_required_shipment = signal<boolean>(true);
    readonly IsRequiredShipment = this._is_required_shipment.asReadonly();

    constructor() {
        effect(() => {
            if (this.countryApiResource.hasValue() && this.isLoggedIn()) {
                const countryvalues = this.countryApiResource.value()?.data;
                const countries = this.profileService.currentCompany()?.countries;
                if (!countries || countries.length === 0 || !countryvalues) {
                    return;
                }

                this._companycountries.set(this.mergeCountriesByIsoCodeFromCatalog(countryvalues, countries));

                // se l'utente e' abilitato si setta il paese di destinazione
                if (this.requiresDestinationCountry()) {
                    const storedIso = this.getStoredCountryIso();
                    const storedCountry = storedIso
                        ? (this._companycountries().find(c => c.iso_code === storedIso) ?? null)
                        : null;

                    if (storedCountry) {
                        this._currentCountry.set(storedCountry);
                    }
                    const storedZip = this.getStoredZipCode();
                    if (storedZip) {
                        this._currentZip.set(storedZip);
                    }
                } else {
                    this._currentCountry.set(null);
                    this.persistCountryIso(null);
                    this.clearCurrentZip();
                }
            }
        });
    }

    /**
     * Setta il linguaggio in uso
     */
    setCurrentLanguage(language: LanguageCode | null | 'clear'): void {
        if (language === 'clear') {
            localStorage.removeItem(this.LANGUAGE_KEY);
            return;
        }
        const defaultLanguage = language ?? this.getDefaultLanguage();
        this.translate.use(defaultLanguage);
        localStorage.setItem(this.LANGUAGE_KEY, defaultLanguage);
        this._currentlanguage.set(defaultLanguage);
    }

    /**
     * Restituisce i linguaggi settabili
     */
    getLanguages() {
        return LANGUAGES;
    }

    getUserToken(): string | null {
        const user = this.currentUser();
        return user?.token ?? null;
    }

    getCompanyToken(): string | null {
        const company = this.profileService.currentCompany();
        return company?.token ?? null;
    }

    getCompanyId(): string | null {
        const company = this.profileService.currentCompany();
        return company?.id ?? null;
    }

    getSelectCountry(iso_code: string): ICountry | null {
        return this.CompanyCountries().find(c => c.iso_code === iso_code) ?? null;
    }

    private persistCountryIso(iso: string | null): void {
        if (!this.localStorage) return;

        if (!iso) {
            this.localStorage.removeItem(this.COUNTRY_KEY);
            return;
        }
        this.localStorage.setItem(this.COUNTRY_KEY, iso);
    }

    private persistZipCode(zip: string | null): void {
        if (!this.localStorage) return;
        if (!zip) {
            this.localStorage.removeItem(this.ZIP_KEY);
            return;
        }
        this.localStorage.setItem(this.ZIP_KEY, zip);
    }

    private getStoredCountryIso(): string | null {
        if (!this.localStorage) return null;
        return this.localStorage.getItem(this.COUNTRY_KEY);
    }

    private getStoredZipCode(): string | null {
        if (!this.localStorage) return null;
        return this.localStorage.getItem(this.ZIP_KEY);
    }

    setCurrentCountryByIso(iso_code: string): void {
        const found = this.CompanyCountries().find(c => c.iso_code === iso_code) ?? null;
        this._currentCountry.set(found);
        this.persistCountryIso(found?.iso_code ?? null);
    }

    setCurrentZip(zip: string | null): void {
        if (zip != null) {
            zip = zip.trim();
        }
        this._currentZip.set(zip);
        this.persistZipCode(zip);
    }

    clearCurrentZip(): void {
        this._currentZip.set(null);
        this.persistZipCode(null);
    }

    setIsRequiredShipment(value: boolean): void {
        this._is_required_shipment.set(value);
    }

    /**
     * Rimuove l'utente dalla sessione e resetta lo stato
     */
    logout(): void {
        this.authService.logout();
        this.profileService.clearProfile();

        // ✅ Pulizia
        this._currentCountry.set(null);
        this._companycountries.set([]);
        this.persistCountryIso(null);
        this.persistZipCode(null);
        this.countryDialog.reset();
        this.requiredShipmentService.reset();
    }

    // restituisce il linguaggio di default da storage o fisso da costante
    private getDefaultLanguage(): string {
        const storedLanguage = localStorage.getItem(this.LANGUAGE_KEY);
        if (storedLanguage) {
            return storedLanguage;
        }
        return DEFAULT_LOCALE;
    }

    // parte solo quando il profilo ha fornito il token utente, altrimenti l'interceptor non può autenticarla
    countryApiResource = httpResource<any | undefined>(() => (this.isLoggedIn() && this.profileService.userToken() ? {
        url: this.endpoints.country.v2.list,
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    } : undefined));

    mergeCountriesByIsoCodeFromCatalog(
        countryvalues: any[],
        companyvalues: string[]
    ): ICountry[] {
        const companyIso = new Set(companyvalues);
        return countryvalues
            .filter(c => companyIso.has(c.iso_code))
            .map(c => ({
                iso_code: c.iso_code,
                name: c.name + ' (' + c.iso_code + ')',
                vat: c.vat ?? 0,
                vat_shipping: c.vat_shipping ?? 0,
                vat_reduced: c.vat_reduced ?? 0,
                vat_reduced_2: c.vat_reduced_2 ?? 0
            })).sort((a, b) => a.name.localeCompare(b.name));
    }
}